import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Target, ChevronDown, ChevronUp, Lightbulb, MessageSquare, AlertCircle, TrendingUp, Sparkles, Loader2 } from 'lucide-react';
import { authHeaders } from '../services/api';

// ─── Rule Engine: generates coaching advice based on context ─────────────────
const generateCoaching = (salesContext, selectedServices, customerInfo) => {
  if (!salesContext) return null;

  const { mood, reason, priority, competition, budget } = salesContext;
  const serviceCount = Object.keys(selectedServices || {}).length;
  const hasMonthly = Object.values(selectedServices || {}).some(s => s.type === 'monatlich' || s.type === 'variabel');
  const hasOneTime = Object.values(selectedServices || {}).some(s => s.type === 'einmalig');
  const customerName = customerInfo?.name || customerInfo?.company || 'der Kunde';

  const tips = [];
  const questions = [];
  const objections = [];
  const closingHints = [];

  // ── MOOD based ──
  if (mood === 'skeptisch') {
    tips.push({ icon: '🛡️', text: 'Skeptischer Kunde: Konkrete Referenzprojekte und Erfolgsgeschichten vorbereiten.' });
    questions.push('Was hat Sie bei früheren Anbietern enttäuscht?');
    questions.push('Was ist für Sie das wichtigste Zeichen, dass wir der richtige Partner sind?');
  } else if (mood === 'neutral') {
    tips.push({ icon: '💡', text: 'Neutraler Kunde: Nutzen und ROI konkret beziffern – zeigen was das Angebot bringt.' });
    questions.push('Was würde sich für Sie ändern, wenn dieses Projekt erfolgreich umgesetzt wird?');
  } else if (mood === 'warm') {
    tips.push({ icon: '🚀', text: 'Positiver Kunde: Momentum nutzen – zügig konkret werden und Entscheidung jetzt ermöglichen.' });
    closingHints.push('Kunden direkt nach dem Gespräch zur Annahme einladen – der Link ist bereits fertig!');
  }

  // ── REASON based ──
  if (reason === 'preisverhandlung') {
    tips.push({ icon: '⚖️', text: 'Preisverhandlung: Wert vor Preis betonen. Gesamtkosten des Nicht-Handelns aufzeigen.' });
    objections.push({
      objection: '"Das ist uns zu teuer"',
      answer: 'Was ist die Alternative? Inhouse-Lösung, Billiganbieter, oder gar nicht? Zeigen Sie den Mehrwert konkret.'
    });
    if (hasMonthly) tips.push({ icon: '📊', text: 'Monatliche Kosten als tagesbasierte Kosten darstellen (z.B. 299€/Monat = ~10€/Tag).' });
  } else if (reason === 'neuprojekt') {
    tips.push({ icon: '🎯', text: 'Neuprojekt: Vollständiges Angebot zeigen – Paketlösung ist oft attraktiver als Einzelpositionen.' });
    closingHints.push('Starttermin konkret nennen: "Wir könnten bereits am [Datum] starten."');
  } else if (reason === 'erweiterung') {
    tips.push({ icon: '🤝', text: 'Bestehender Kunde: Auf bisherige Erfolge referenzieren, Loyalty-Aspekt hervorheben.' });
    questions.push('Was hat bisher am besten funktioniert, was möchten Sie ausbauen?');
  }

  // ── PRIORITY based ──
  if (priority === 'preis') {
    objections.push({
      objection: '"Gibt es einen Rabatt?"',
      answer: 'Ja, bei Paketen oder längeren Laufzeiten. Staffelpreise können günstiger sein – mehr Menge = besser.'
    });
    if (serviceCount > 3) tips.push({ icon: '📦', text: 'Pakete hervorheben: Bündelrabatt statt Einzelpositionen.' });
  } else if (priority === 'schnelligkeit') {
    tips.push({ icon: '⚡', text: 'Schnelligkeit als USP: Genau aufzeigen wann Ergebnisse geliefert werden.' });
    closingHints.push('Projektplan mit konkreten Meilensteinen und Liefertermine vorstellen.');
  } else if (priority === 'qualitaet') {
    tips.push({ icon: '🏆', text: 'Qualitätsorientiert: Detaillierte Leistungsbeschreibungen aktivieren – Klick auf "Detaillierte Beschreibungen".' });
    questions.push('Was ist Ihr Qualitätsmaßstab? Woran erkennen Sie exzellente Arbeit?');
  } else if (priority === 'betreuung') {
    tips.push({ icon: '🤗', text: 'Betreuungsorientiert: Monatliche Services und Support-Leistungen besonders hervorheben.' });
    if (!hasMonthly) tips.push({ icon: '💡', text: 'Erwägen Sie, eine monatliche Wartung/Betreuungsposition hinzuzufügen.' });
  }

  // ── COMPETITION based ──
  if (competition === 'ja') {
    tips.push({ icon: '🏁', text: 'Wettbewerber im Rennen: Ihren einzigartigen Differenziator klar kommunizieren.' });
    questions.push('Was war an anderen Angeboten, die Sie bekommen haben, noch nicht überzeugend?');
    objections.push({
      objection: '"Wir haben ein günstigeres Angebot"',
      answer: 'Vergleichen Sie die Leistungsumfänge genau. Günstig und vollständig ist selten beides – was ist im anderen Angebot nicht enthalten?'
    });
  }

  // ── BUDGET based ──
  if (budget === 'eingeschraenkt') {
    tips.push({ icon: '🎯', text: 'Eingeschränktes Budget: Priorisierung anbieten – Was ist Phase 1, was kann später kommen?' });
    closingHints.push('Phasenmodell vorschlagen: "Wir starten mit dem Kern, Erweiterungen folgen."');
  } else if (budget === 'unklar') {
    questions.push('Haben Sie bereits ein Budget-Rahmen für dieses Projekt im Kopf?');
    tips.push({ icon: '❓', text: 'Budget nicht bekannt: Früh im Gespräch nach der Budget-Range fragen.' });
  }

  // ── Service-Count hints ──
  if (serviceCount === 0) {
    tips.push({ icon: '📋', text: 'Noch keine Services gewählt. Wählen Sie Services im Kalkulator links.' });
  } else if (serviceCount > 6) {
    tips.push({ icon: '✂️', text: `${serviceCount} Positionen – viele! Überlegen ob alle notwendig sind oder ob ein Paket übersichtlicher wäre.` });
  }

  return { tips, questions, objections, closingHints };
};

// ─── Scoring: Abschluss-Wahrscheinlichkeit ───────────────────────────────────
const getScore = (salesContext, selectedServices) => {
  if (!salesContext) return null;
  let score = 60;
  if (salesContext.mood === 'warm') score += 15;
  if (salesContext.mood === 'skeptisch') score -= 10;
  if (salesContext.budget === 'offen') score += 10;
  if (salesContext.budget === 'eingeschraenkt') score -= 10;
  if (salesContext.competition === 'nein') score += 10;
  if (salesContext.competition === 'ja') score -= 5;
  if (salesContext.reason === 'neuprojekt') score += 5;
  if (salesContext.reason === 'preisverhandlung') score -= 5;
  if (Object.keys(selectedServices || {}).length > 0) score += 5;
  return Math.max(20, Math.min(95, score));
};

const ScoreBar = ({ score, salesContext }) => {
  const color = score >= 75 ? 'bg-green-500' : score >= 55 ? 'bg-yellow-500' : 'bg-orange-500';
  const label = score >= 75 ? 'Hoch' : score >= 55 ? 'Mittel' : 'Niedrig';
  const [showDetails, setShowDetails] = React.useState(false);
  
  const factors = [
    { label: 'Stimmung warm', value: salesContext?.mood === 'warm' ? '+15%' : salesContext?.mood === 'skeptisch' ? '-10%' : '±0%', active: salesContext?.mood !== 'neutral' },
    { label: 'Budget offen', value: salesContext?.budget === 'offen' ? '+10%' : salesContext?.budget === 'eingeschraenkt' ? '-10%' : '±0%', active: salesContext?.budget !== 'unklar' },
    { label: 'Kein Wettbewerb', value: salesContext?.competition === 'nein' ? '+10%' : salesContext?.competition === 'ja' ? '-5%' : '±0%', active: salesContext?.competition !== 'unbekannt' },
    { label: 'Gesprächsgrund', value: salesContext?.reason === 'neuprojekt' ? '+5%' : salesContext?.reason === 'preisverhandlung' ? '-5%' : '±0%', active: true },
    { label: 'Services gewählt', value: '+5%', active: true },
  ];
  
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500">Abschluss-Wahrscheinlichkeit</span>
          <button onClick={() => setShowDetails(!showDetails)} className="text-slate-400 hover:text-blue-500 transition-colors" title="Wie wird der Score berechnet?">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
          </button>
        </div>
        <span className="text-xs font-bold text-slate-700">{score}% — {label}</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${score}%` }} />
      </div>
      {showDetails && (
        <div className="mt-2 p-2 bg-slate-50 rounded-lg border border-slate-100 text-xs space-y-1">
          <p className="font-semibold text-slate-600 mb-1">Score-Faktoren (Basis: 60%):</p>
          {factors.map((f, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-slate-600">{f.label}</span>
              <span className={f.value.startsWith('+') ? 'text-green-600 font-medium' : f.value.startsWith('-') ? 'text-red-500 font-medium' : 'text-slate-400'}>{f.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const CoachingWidget = ({ salesContext, selectedServices, customerInfo, onReset }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTabLocal] = useState('ki');
  const [aiCoaching, setAiCoaching] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Fetch AI coaching tips when panel opens or context/services change
  const fetchAiTips = useCallback(async () => {
    if (!salesContext || !isOpen) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coaching/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ salesContext, selectedServices, customerInfo })
      });
      const data = await resp.json();
      if (data.success && data.coaching) {
        setAiCoaching(data.coaching);
      } else {
        setAiError('KI-Tipps konnten nicht geladen werden');
      }
    } catch {
      setAiError('Verbindungsfehler');
    } finally {
      setAiLoading(false);
    }
  }, [salesContext, selectedServices, customerInfo, isOpen]);

  useEffect(() => {
    if (isOpen && !aiCoaching) fetchAiTips();
  }, [isOpen, fetchAiTips, aiCoaching]);

  // Refresh AI tips when services change (with debounce)
  const serviceCount = Object.keys(selectedServices || {}).length;
  useEffect(() => {
    if (isOpen && serviceCount > 0) {
      const timer = setTimeout(() => {
        setAiCoaching(null);
        fetchAiTips();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [serviceCount]);

  const coaching = useMemo(
    () => generateCoaching(salesContext, selectedServices, customerInfo),
    [salesContext, selectedServices, customerInfo]
  );
  const score = useMemo(
    () => aiCoaching?.score || getScore(salesContext, selectedServices),
    [salesContext, selectedServices, aiCoaching]
  );
  const scoreReason = aiCoaching?.scoreReason || null;

  if (!salesContext) return null;

  const tabItems = [
    { key: 'ki', label: 'KI-Analyse', icon: Sparkles, count: aiCoaching ? 1 : 0 },
    { key: 'tipps', label: 'Tipps', icon: Lightbulb, count: coaching?.tips?.length },
    { key: 'fragen', label: 'JA-Fragen', icon: MessageSquare, count: (coaching?.questions?.length || 0) + (aiCoaching?.bestQuestion ? 1 : 0) },
    { key: 'einwaende', label: 'Einwände', icon: AlertCircle, count: coaching?.objections?.length },
    { key: 'abschluss', label: 'Abschluss', icon: TrendingUp, count: coaching?.closingHints?.length },
  ].filter(t => t.count > 0 || t.key === 'ki');

  return (
    <div className="fixed bottom-14 right-6 z-50 no-print" data-testid="coaching-widget">
      {/* Collapsed Trigger */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="coaching-widget-open"
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-2xl shadow-2xl hover:shadow-blue-200 hover:scale-105 transition-all"
        >
          <Target className="w-5 h-5" />
          <span className="font-semibold text-sm">Sales-Coach</span>
          {score && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              score >= 75 ? 'bg-green-400/30' : score >= 55 ? 'bg-yellow-400/30' : 'bg-orange-400/30'
            }`}>
              {score}%
            </span>
          )}
          <ChevronUp className="w-4 h-4" />
        </button>
      )}

      {/* Expanded Panel */}
      {isOpen && (
        <div className="w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col"
          style={{ maxHeight: '520px' }}
          data-testid="coaching-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-white" />
              <span className="font-bold text-white text-sm">Sales-Coach</span>
              {customerInfo?.name && (
                <span className="text-blue-200 text-xs">· {customerInfo.name}</span>
              )}
            </div>
            <button onClick={() => setIsOpen(false)} className="text-blue-200 hover:text-white transition-colors">
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Score */}
          <div className="px-4 pt-3 pb-0">
            {score && <ScoreBar score={score} salesContext={salesContext} />}
            {scoreReason && <p className="text-xs text-slate-500 -mt-2 mb-2 italic">{scoreReason}</p>}
          </div>

          {/* Context Badges */}
          <div className="px-4 pb-3 flex flex-wrap gap-1.5">
            {salesContext.mood && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {salesContext.mood === 'warm' ? '😊' : salesContext.mood === 'neutral' ? '😐' : '😒'} {salesContext.mood}
              </span>
            )}
            {salesContext.priority && (
              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                ⭐ {salesContext.priority}
              </span>
            )}
            {salesContext.competition === 'ja' && (
              <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">
                🏁 Wettbewerb
              </span>
            )}
            {salesContext.budget === 'eingeschraenkt' && (
              <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">
                💰 Budget knapp
              </span>
            )}
          </div>

          {/* Tabs */}
          {tabItems.length > 0 && (
            <div className="flex border-b border-slate-100 px-2">
              {tabItems.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTabLocal(t.key)}
                  className={`flex items-center gap-1 px-2.5 py-2 text-xs font-medium transition-colors border-b-2 ${
                    activeTab === t.key
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`coaching-tab-${t.key}`}
                >
                  <t.icon className="w-3 h-3" />
                  {t.label}
                  {t.count > 0 && (
                    <span className={`text-xs px-1 rounded-full ${activeTab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                      {t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            
            {/* KI Analysis Tab */}
            {activeTab === 'ki' && (
              aiLoading ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  <p className="text-xs text-slate-500">KI analysiert Ihre Situation...</p>
                </div>
              ) : aiError ? (
                <div className="text-center py-4">
                  <p className="text-xs text-red-500 mb-2">{aiError}</p>
                  <button onClick={fetchAiTips} className="text-xs text-blue-600 hover:underline">Erneut versuchen</button>
                </div>
              ) : aiCoaching ? (
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-bold text-blue-700">Top-Empfehlung</span>
                    </div>
                    <p className="text-sm text-slate-800 leading-relaxed font-medium">{aiCoaching.topTip}</p>
                  </div>
                  {aiCoaching.tips?.map((tip, i) => (
                    <div key={i} className="flex gap-2 text-sm">
                      <span className="text-blue-400 shrink-0">✦</span>
                      <p className="text-slate-700">{tip}</p>
                    </div>
                  ))}
                  {aiCoaching.bestQuestion && (
                    <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                      <p className="text-xs font-semibold text-green-700 mb-1">Beste JA-Frage:</p>
                      <p className="text-sm text-green-800 italic">&bdquo;{aiCoaching.bestQuestion}&ldquo;</p>
                    </div>
                  )}
                  {aiCoaching.mainObjection && (
                    <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                      <p className="text-xs font-semibold text-orange-700 mb-1">Haupteinwand & Antwort:</p>
                      <p className="text-sm text-slate-700">{aiCoaching.mainObjection}</p>
                    </div>
                  )}
                  {aiCoaching.closingHint && (
                    <div className="flex gap-2 text-sm">
                      <TrendingUp className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                      <p className="text-slate-700">{aiCoaching.closingHint}</p>
                    </div>
                  )}
                  <button onClick={() => { setAiCoaching(null); fetchAiTips(); }}
                    className="text-xs text-slate-400 hover:text-blue-600 flex items-center gap-1 mt-2">
                    <Sparkles className="w-3 h-3" /> Neu generieren
                  </button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <button onClick={fetchAiTips}
                    className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto">
                    <Sparkles className="w-4 h-4" /> KI-Analyse starten
                  </button>
                </div>
              )
            )}

            {activeTab === 'tipps' && coaching?.tips?.map((tip, i) => (
              <div key={i} className="flex gap-2 text-sm">
                <span className="text-base shrink-0">{tip.icon}</span>
                <p className="text-slate-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}

            {activeTab === 'fragen' && (
              <>
                {aiCoaching?.bestQuestion && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mb-1">
                    <div className="flex items-center gap-1 mb-1">
                      <Sparkles className="w-3 h-3 text-blue-500" />
                      <span className="text-xs text-blue-600 font-medium">KI-Empfehlung</span>
                    </div>
                    <p className="text-sm text-blue-800 font-medium">❓ &bdquo;{aiCoaching.bestQuestion}&ldquo;</p>
                  </div>
                )}
                {coaching?.questions?.map((q, i) => (
                  <div key={i} className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-800 font-medium">❓ &bdquo;{q}&ldquo;</p>
                  </div>
                ))}
              </>
            )}

            {activeTab === 'einwaende' && coaching?.objections?.map((o, i) => (
              <div key={i} className="bg-orange-50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-orange-800">Kunde: {o.objection}</p>
                <p className="text-sm text-slate-700">→ {o.answer}</p>
              </div>
            ))}

            {activeTab === 'abschluss' && coaching?.closingHints?.map((h, i) => (
              <div key={i} className="flex gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <p className="text-slate-700">{h}</p>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-slate-100 flex justify-between items-center">
            <button onClick={onReset} className="text-xs text-slate-400 hover:text-blue-600 transition-colors flex items-center gap-1"
              data-testid="coaching-reset-btn">
              <Target className="w-3 h-3" /> Neu kalibrieren
            </button>
            <button onClick={() => setIsOpen(false)} className="text-xs text-slate-400 hover:text-slate-600">
              Einklappen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachingWidget;
